import _ from "lodash";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

type OptionsType<T> = {
  items: T[];
  initialIndex?: number;
  onEscape?: () => void;
  onEnter?: (item?: T) => void;
};

type ReturnType<T> = {
  selectedItem?: T;
  resetIndex: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
};

export const useListKeyNavigation = <T>({
  items,
  initialIndex = -1,
  onEscape,
  onEnter,
}: OptionsType<T>): ReturnType<T> => {
  const getBoundedIndex = (index: number, itemCount: number) => {
    if (itemCount === 0 || index < 0) return -1;
    return _.clamp(index, 0, itemCount - 1);
  };

  const [searchIndex, setSearchIndex] = useState(() =>
    getBoundedIndex(initialIndex, items.length),
  );
  const searchIndexRef = useRef(searchIndex);
  const itemsRef = useRef(items);
  const initialIndexRef = useRef(initialIndex);
  const onEscapeRef = useRef(onEscape);
  const onEnterRef = useRef(onEnter);

  searchIndexRef.current = searchIndex;
  itemsRef.current = items;
  initialIndexRef.current = initialIndex;
  onEscapeRef.current = onEscape;
  onEnterRef.current = onEnter;

  useEffect(() => {
    setSearchIndex((currentIndex) => {
      const boundedIndex = getBoundedIndex(currentIndex, items.length);
      return boundedIndex === currentIndex ? currentIndex : boundedIndex;
    });
  }, [items.length]);

  const updateSearchIndex = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case "Escape":
          onEscapeRef.current?.();
          break;
        case "Enter":
          onEnterRef.current?.(itemsRef.current[searchIndexRef.current]);
          break;
        case "ArrowUp":
        case "ArrowDown":
          event.preventDefault();
          event.stopPropagation();
          setSearchIndex((currentIndex) =>
            getBoundedIndex(
              currentIndex + (event.key === "ArrowUp" ? -1 : 1),
              itemsRef.current.length,
            ),
          );
          break;
      }
    },
    [],
  );

  const resetIndex = useCallback(() => {
    setSearchIndex(
      getBoundedIndex(initialIndexRef.current, itemsRef.current.length),
    );
  }, []);

  return {
    selectedItem: items[searchIndex],
    onKeyDown: updateSearchIndex,
    resetIndex,
  };
};
